import { SupportedLocaleCode } from '@constants/locales';

//~ Define your core data model interfaces for this feature

export interface SourceOption<T = unknown> {
    key: string; // 'tmdb', 'viva', 'proscope', 'user_overrides'
    value: T;
    disabled?: boolean;
}

export type Sources = 'user_overrides' | 'proscope' | 'viva' | 'tmdb';
export type MultilingualMetadataSource = Record<
    Sources,
    MultilingualCollectionMetadata
>;
export type MetadataSources = Record<Sources, CollectionMetadata>;

export type CreditRole = 'actors' | 'directors' | 'writers' | 'music';
export type Credits = Record<CreditRole, string[]>;

export type SynopsisType = 'short' | 'full';
export type Synopses = Record<SynopsisType, string>;

export type ImageType = 'background' | 'cover' | 'landscape'; // | 'low' | 'medium' | 'high';
export interface Image {
    width: number;
    height: number;
    url: string;
}
export type Images = Record<ImageType, Image>;

export type LocalizedType<T> = Record<SupportedLocaleCode, T>;
export type LocalizedTitle = LocalizedType<string>;
export type LocalizedGenres = LocalizedType<string[]>;
export type LocalizedSynopses = LocalizedType<Synopses>;

export interface CollectionMetadata {
    proscopeId: string;
    collectionId: string;
    title: string;
    duration: string;
    releaseYear: string;
    productionYear: string;
    country: string;
    genres: string[];
    credits: Credits;
    synopsis: Synopses;
    images: Images;
    trailers: string[];
}

export interface MultilingualCollectionMetadata {
    proscopeId: string;
    collectionId: string;
    title: LocalizedTitle;
    duration: string;
    releaseYear: string;
    productionYear: string;
    country: string;
    genres: LocalizedGenres;
    credits: Credits;
    synopsis: LocalizedSynopses;
    images: Images;
    trailers: string[];
}

//~ Define Backend response types if they differ significantly from your frontend model
export type BackendSynopses = Record<SynopsisType, string | undefined>;
type LocalizedBackendSynopses = LocalizedType<BackendSynopses>;
type BackendImages = Record<ImageType, Image | undefined>;

export interface MamutilsBaseSchema {
    source: string;
    id: string | number | undefined;
    originalTitle: string | undefined;
    originalLanguage: string | undefined;
    releaseYear: number | undefined;
    productionYear: number | undefined;
    country: string | undefined;
    duration: string | undefined;
    ageRating: string | undefined;
    credits: Credits;
    title: LocalizedTitle;
    genres: LocalizedGenres;
    synopsis: LocalizedBackendSynopses;
    images: BackendImages;
    trailers: string[];
}

export interface BackendCollectionMetadata {
    sources: Record<Sources, MamutilsBaseSchema>;
}

export interface BackendSaveOverrideResponse {
    message: string;
    proscopeId: string;
    author: string;
    savedData: Partial<MamutilsBaseSchema>;
}

export interface OverrideHistoryItem {
    id: string;
    timestamp: string;
    author: string;
    data: Partial<CollectionMetadata>;
}

export interface GetOverrideHistoryResponse {
    proscopeId: string;
    totalEdits: number;
    history: OverrideHistoryItem[];
}

export interface GetCollectionMetadataResponse {
    merged: MultilingualCollectionMetadata;
    sources: MultilingualMetadataSource;
}

export interface SaveCollectionOverrideResponse {
    message: string;
    proscopeId: string;
    author: string;
    savedData: MultilingualCollectionMetadata;
}

//~ Define specific query arguments if not in the API file direclty
export interface GetCollectionMetadataArgs {
    proscopeId: string;
    collectionId: string;
    nocache?: boolean;
}

export interface GetOverrideHistoryArgs {
    proscopeId: string;
}

export interface SaveCollectionOverrideArgs {
    proscopeId: string;
    collectionId: string;
    userId: string;
    overrides: Partial<CollectionMetadata>;
}

//~ Constants

export const DEFAULT_CREDITS: Credits = {
    actors: [],
    directors: [],
    music: [],
    writers: [],
};

export const DEFAULT_SYNOPSIS: Synopses = {
    short: '',
    full: '',
};

export const DEFAULT_LOCALIZED_SYNOPSES: LocalizedSynopses = {
    en: DEFAULT_SYNOPSIS,
    fr: DEFAULT_SYNOPSIS,
};

export const DEFAULT_IMAGE: Image = {
    height: 0,
    width: 0,
    url: '',
};

export const DEFAULT_IMAGES: Images = {
    background: DEFAULT_IMAGE,
    cover: DEFAULT_IMAGE,
    landscape: DEFAULT_IMAGE,
    // low: DEFAULT_IMAGE,
    // medium: DEFAULT_IMAGE,
    // high: DEFAULT_IMAGE,
};

export const DEFAULT_LOCALIZED_TITLES: LocalizedTitle = {
    en: '',
    fr: '',
};

export const DEFAULT_LOCALIZED_GENRES: LocalizedGenres = {
    en: [],
    fr: [],
};

export const DEFAULT_MULTILINGUAL_METADATA: MultilingualCollectionMetadata = {
    proscopeId: '',
    collectionId: '',
    title: DEFAULT_LOCALIZED_TITLES,
    duration: '',
    releaseYear: '',
    productionYear: '',
    country: '',
    genres: DEFAULT_LOCALIZED_GENRES,
    credits: DEFAULT_CREDITS,
    synopsis: DEFAULT_LOCALIZED_SYNOPSES,
    images: DEFAULT_IMAGES,
    trailers: [],
};

export const DEFAULT_METADATA: CollectionMetadata = {
    proscopeId: '',
    collectionId: '',
    title: '',
    duration: '',
    releaseYear: '',
    productionYear: '',
    country: '',
    genres: [],
    credits: DEFAULT_CREDITS,
    synopsis: DEFAULT_SYNOPSIS,
    images: DEFAULT_IMAGES,
    trailers: [],
};

export const DEFAULT_OVERRIDE_HISTORY_RESPONSE: GetOverrideHistoryResponse = {
    proscopeId: '',
    totalEdits: 0,
    history: [],
};

export const DEFAULT_COLLECTION_METADATA_SOURCES: GetCollectionMetadataResponse['sources'] =
    {
        user_overrides: DEFAULT_MULTILINGUAL_METADATA,
        proscope: DEFAULT_MULTILINGUAL_METADATA,
        viva: DEFAULT_MULTILINGUAL_METADATA,
        tmdb: DEFAULT_MULTILINGUAL_METADATA,
    };

export const DEFAULT_COLLECTION_METADATA_RESPONSE: GetCollectionMetadataResponse =
    {
        merged: DEFAULT_MULTILINGUAL_METADATA,
        sources: DEFAULT_COLLECTION_METADATA_SOURCES,
    };
